import { Box, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { QRCodeSVG } from "qrcode.react";
import type React from "react";
import { ONBOARDING_COLORS } from "../../features/organisations/onboarding/onboardingDesign.js";
import { FittedCode } from "./FittedCode.js";

interface Props {
  /** The code as it is displayed, e.g. `K7P-4QX` or `WDJB-MJHT`. */
  code: string;
  /** What the QR code encodes. */
  qrValue: string;
  "data-testid"?: string;
}

const QR_SIZE = { xs: 200, desktop: 240 };

const Half = ({
  label,
  isSizeContainer = false,
  children,
}: React.PropsWithChildren<{ label: string; isSizeContainer?: boolean }>) => (
  <Stack sx={{ minWidth: 0, p: { xs: 2, desktop: 3 }, gap: 2 }}>
    <Typography
      component="h2"
      sx={{
        fontSize: 13,
        fontWeight: 500,
        lineHeight: "20px",
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        textAlign: "center",
        color: ONBOARDING_COLORS.muted,
      }}
    >
      {label}
    </Typography>
    <Box
      sx={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 0,
        // The code sizes itself to this box, not to the viewport. A size
        // container has no intrinsic width, so the QR half must not be one.
        ...(isSizeContainer && { containerType: "inline-size" }),
      }}
    >
      {children}
    </Box>
  </Stack>
);

/**
 * One card with two equally tall halves: the code to type in on the left and
 * the QR code to scan instead on the right (stacked below the desktop
 * breakpoint). Shown on a fair-stand PC and read from a few metres.
 */
export const CodeOrScanCard = ({
  code,
  qrValue,
  "data-testid": testId,
}: Props) => {
  const { t } = useTranslate(["Global"]);
  return (
    <Box
      sx={{
        width: "100%",
        display: "grid",
        gridTemplateColumns: {
          xs: "minmax(0, 1fr)",
          desktop: "minmax(0, 1fr) auto",
        },
        border: 1,
        borderColor: ONBOARDING_COLORS.border,
        borderRadius: "8px",
        bgcolor: "common.white",
        // The divider between the halves.
        "& > :last-of-type": {
          borderStyle: "solid",
          borderColor: ONBOARDING_COLORS.border,
          borderWidth: { xs: "1px 0 0", desktop: "0 0 0 1px" },
        },
      }}
    >
      <Half label={t("Enter the code", { ns: "Global" })} isSizeContainer>
        <FittedCode
          code={code}
          maxFontSize="6rem"
          aria-live="polite"
          data-testid={testId}
        />
      </Half>
      <Half label={t("or scan", { ns: "Global" })}>
        <Box
          sx={{ display: "flex", "& svg": { width: QR_SIZE, height: "auto" } }}
        >
          <QRCodeSVG value={qrValue} size={QR_SIZE.desktop} />
        </Box>
      </Half>
    </Box>
  );
};
